import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant,
  ErrorHappened,
  FormItem,
  HeadingSize,
  InputType,
  Label,
  LabelType,
  Loading,
  TabPanel,
  useKeyboardShortcut,
  useToast
} from '@fs/uikit-react';
import styles from './PersonEdit.module.css';
import {ApiError} from "@/services/api";
import {
  Gender,
  MaritalStatus
} from "@/pages/Operator/Person/types/personConstants.ts";
import {Person} from "@/pages/Operator/Person/types/person.ts";
import {PersonApi} from "@/pages/Operator/Person/api/PersonApi.ts";
import PersonNotes
  from "@/pages/Operator/Person/components/edit/PersonNotes/PersonNotes.tsx";
import PersonMainTab from './PersonMainTab';
import PersonContacts from '@/pages/Operator/Person/components/edit/PersonContacts/PersonContacts.tsx';
import {
  usePersonModalCategories
} from '@/pages/Operator/Person/components/modals/PersonModal/usePersonModalCategories.ts';
import {CategoryShort} from '@/shared/model/categoryShort.ts';

export const PersonEdit: React.FC = () => {
  const {id} = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);

  const [person, setPerson] = useState<Person | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fathersName, setFathersName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<Gender>(Gender.MALE);
  const [nationality, setNationality] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [maritalStatus, setMaritalStatus] = useState<MaritalStatus>(MaritalStatus.SINGLE);
  const [occupation, setOccupation] = useState('');
  const [comment, setComment] = useState('');

  // Categories state for main tab
  const [categories, setCategories] = useState<CategoryShort[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Fetch categories for multiselect
  usePersonModalCategories(setCategories);

  const categoryOptions = useMemo(() => (
    categories.map((c) => ({ value: c.id, label: c.name }))
  ), [categories]);

  const selectedCategories: CategoryShort[] = useMemo(() => (
    categories.filter(c => selectedCategoryIds.includes(c.id))
  ), [categories, selectedCategoryIds]);

  useEffect(() => {
    const fetchPerson = async () => {
      if (!id) return;

      try {
        setIsFetching(true);
        setError(null);
        const response = await PersonApi.getById(id);
        const person: Person = response.data as Person;
        setPerson(person);

        setFirstName(person?.firstName || '');
        setLastName(person?.lastName || '');
        setFathersName(person.fathersName || '');
        setDateOfBirth(person.dateOfBirth || '');
        setGender(person.gender as Gender);
        setNationality(person.nationality || '');
        setIdentificationNumber(person.identificationNumber || '');
        setMaritalStatus((person.maritalStatus as MaritalStatus) || MaritalStatus.SINGLE);
        setOccupation(person.occupation || '');
        setComment(person.comment || '');
        setSelectedCategoryIds(person.categories ? person.categories.map(c => c.id) : []);
      } catch (err) {
        setError(err as ApiError);
        console.error('Error fetching person:', err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchPerson();
  }, [id]);

  const handleSubmit = async () => {
    if (!id) return;

    if (!firstName && !lastName) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, заповніть хоча б Прізвище або Ім\'я');
      return;
    }

    setIsLoading(true);

    const personData: Person = {
      firstName,
      lastName,
      fathersName,
      dateOfBirth,
      gender,
      nationality,
      identificationNumber,
      maritalStatus,
      occupation,
      comment,
      categories: selectedCategories
    };

    try {
      await PersonApi.update(id, personData);
      toast.showSuccessToast('Успішно', 'Особу успішно оновлено');
    } catch (error) {
      console.error('Error updating person:', error);
      toast.showErrorToast(
        'Помилка оновлення особи',
        getUserFriendlyErrorMessage(error as ApiError)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/operator/person');
  };

  useKeyboardShortcut('Escape', [], handleCancel);

  if (isFetching) {
    return <Loading text="Загружаємо дані особи..."
                    className={styles.loadingState}/>;
  }

  if (error) {
    return <ErrorHappened text={getUserFriendlyErrorMessage(error)}/>;
  }

  if (!person) {
    return <ErrorHappened text="Особу не знайдено"/>;
  }

  const fullName = [lastName, firstName, fathersName].filter(Boolean).join(' ');

  const tabs = [
    {id: 'main', label: 'Основна'},
    {id: 'official', label: 'Офіційна'},
    {id: 'contacts', label: 'Контакти'},
    {id: 'relations', label: 'Зв\'язки'},
    {id: 'images', label: 'Зображення'},
    {id: 'notes', label: 'Нотатки'},
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Label
          text={fullName}
          type={LabelType.HEADING}
          headingSize={HeadingSize.L}
        />
      </div>

      <TabPanel
        tabs={tabs}
        initialActiveTab={activeTab}
        onTabChange={(tabId) => { setActiveTab(tabId); }}
        className={styles.tabs}
      />

      <div>
        <div className={styles.formSection}>
          {activeTab === 0 && (
            <PersonMainTab
              firstName={firstName}
              lastName={lastName}
              fathersName={fathersName}
              dateOfBirth={dateOfBirth}
              gender={gender}
              maritalStatus={maritalStatus}
              comment={comment}
              categoryOptions={categoryOptions}
              selectedCategoryIds={selectedCategoryIds}
              onCategoriesChange={setSelectedCategoryIds}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
              onFathersNameChange={setFathersName}
              onDateOfBirthChange={setDateOfBirth}
              onGenderChange={(v) => setGender(v)}
              onMaritalStatusChange={(v) => setMaritalStatus(v)}
              onCommentChange={setComment}
            />
          )}

          {activeTab === 1 && (
            <>
              <div className={styles.row}>
                <FormItem
                  label="ІПН/Паспорт"
                  type={InputType.TEXT}
                  value={identificationNumber}
                  onChange={(e) => { setIdentificationNumber(e.target.value); }}
                />
                <FormItem
                  label="Національність"
                  type={InputType.TEXT}
                  value={nationality}
                  onChange={(e) => { setNationality(e.target.value); }}
                />
              </div>
              <div className={styles.row}>
                <FormItem
                  label="Діяльність"
                  type={InputType.TEXT}
                  value={occupation}
                  onChange={(e) => { setOccupation(e.target.value); }}
                />
              </div>
            </>
          )}

          {activeTab === 2 && (
            <PersonContacts personId={id} />
          )}

          {activeTab === 3 && (
            <div>Вкладка Зв'язки (у розробці)</div>
          )}

          {activeTab === 4 && (
            <div>Вкладка Зображення (у розробці)</div>
          )}

          {activeTab === 5 && (
            <PersonNotes personId={id} />
          )}
        </div>

        <div className={styles.buttonGroup}>
          <Button
            title={isLoading ? 'Збереження...' : 'Зберегти'}
            onClick={handleSubmit}
            loading={isLoading}
          />
          <Button
            title="Скасувати"
            onClick={handleCancel}
            variant={ButtonVariant.SECONDARY}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonEdit;
